import React from 'react';
import { CalendarDays, Clock } from 'lucide-react';
import { formatDay, formatDayTime } from '../../utils/dateUtils';

/**
 * Styled date / time pickers for new Tailwind code (SAM-175 freeze: no BaseUI DatePicker). The
 * native picker underneath keeps the platform calendar / clock (and the phone's wheel on iOS and
 * Android); the box matches the editor v2 inputs: 40px tall, 8px radius, line border, white, with
 * our own lucide icon in place of the browser's indicator. A click anywhere in the box opens the
 * picker (showPicker, where supported). No Tailwind preflight in this app, so border style and
 * background are explicit.
 */

export const fieldBox =
  'h-10 w-full min-w-0 rounded-[8px] border border-solid bg-white text-[14px] text-[var(--ink-900)] outline-none '
  + 'focus:border-[var(--tenant-primary,#005488)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--tenant-primary,#005488)_15%,transparent)]';

interface BaseProps {
  label?: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  className?: string;
  min?: string;
  max?: string;
}

const PickerField: React.FC<BaseProps & {
  type: 'date' | 'time' | 'datetime-local'; icon: React.ReactNode; width?: string; display?: string; placeholder?: string;
}> = ({
  label, ariaLabel, value, onChange, invalid, className = '', type, icon, width, min, max, display, placeholder,
}) => {
  // With `display`, the native input stays on top (it still opens the platform calendar and keeps
  // its accessibility) but its own text is transparent: what shows is our label, so the format
  // never depends on the browser's locale (mm/dd/yyyy vs dd/mm/yyyy).
  const overlay = display !== undefined;
  const input = (
    <span className={`relative flex items-center ${width ?? 'w-full'}`}>
      <span aria-hidden className="pointer-events-none absolute left-2.5 flex text-[var(--ink-400)]">{icon}</span>
      {overlay && (
        <span
          aria-hidden
          className={`pointer-events-none absolute left-8 right-2 truncate text-[14px] ${display ? 'text-[var(--ink-900)]' : 'text-[var(--ink-400)]'}`}
        >
          {display || placeholder}
        </span>
      )}
      <input
        type={type}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => {
          try { (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch { /* not allowed here */ }
        }}
        className={`${fieldBox} pl-8 pr-2 cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:w-0 ${overlay ? 'text-transparent [&::-webkit-datetime-edit]:opacity-0' : ''} ${invalid ? 'border-[var(--danger,#c0392b)]' : 'border-[var(--line)]'}`}
      />
    </span>
  );
  if (!label) return <span className={className}>{input}</span>;
  return (
    <label className={`flex flex-col gap-1 text-xs min-w-0 ${className}`}>
      <span className="text-[var(--ink-500)]">{label}</span>
      {input}
    </label>
  );
};

/** A day (`YYYY-MM-DD`), always shown with the month written out ("Oct 1, 2026"). */
export const DateField: React.FC<BaseProps & { placeholder?: string }> = ({ placeholder = 'Pick a date', ...props }) => (
  <PickerField
    {...props}
    type="date"
    icon={<CalendarDays size={15} />}
    display={formatDay(props.value)}
    placeholder={placeholder}
  />
);

/** A day and time (`YYYY-MM-DDTHH:mm`), always shown month-written ("Oct 3, 2026, 7:00 PM"). */
export const DateTimeField: React.FC<BaseProps & { placeholder?: string }> = ({ placeholder = 'Pick a date and time', ...props }) => (
  <PickerField
    {...props}
    type="datetime-local"
    icon={<CalendarDays size={15} />}
    display={formatDayTime(props.value)}
    placeholder={placeholder}
  />
);

/** A clock time (`HH:MM`). Narrow by default so two fit on a 390px row with "to" between. */
export const TimeField: React.FC<BaseProps & { width?: string }> = ({ width = 'w-[124px]', ...props }) => (
  <PickerField {...props} type="time" icon={<Clock size={15} />} width={width} />
);
